// ============================================
// Path: /frontend/src/pages/VehiclesPage.jsx
// Purpose: Vehicles landing page with search filter and featured car yards
// UPDATED: Mobile-first responsive styling + sorting + search + rotation
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { VehiclesSearchFilter } from '../components/VehiclesSearchFilter';
import { Star, Search, ArrowUpDown } from 'lucide-react';
import { Separator } from '../components/ui/separator';
import { AgentCard, AgentCardSkeleton } from '../components/AgentCard';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { usersAPI } from '../api/users.api';
import { CategoryToggle } from '../components/CategoryToggle';

// ========================================
// HELPER FUNCTIONS
// ========================================
// Fisher-Yates shuffle algorithm for random rotation
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// ========================================
// FEATURED CAR YARDS COMPONENT
// ========================================
const FeaturedCarYards = () => {
  console.log('🎨 [FeaturedCarYards] Component mounted');

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [allDealers, setAllDealers] = useState([]);
  const [displayedDealers, setDisplayedDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('random');
  const [rotationKey, setRotationKey] = useState(Date.now());

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchFeaturedDealers = async () => {
      console.log('🔵 [FeaturedCarYards] Fetching featured car yards...');
      setLoading(true);
      
      try {
        // Fetch ALL Gold and Platinum dealers (no limit on fetch)
        const data = await usersAPI.getFeaturedDealers({
          subscriptionTier: 'GOLD,PLATINUM',
          limit: 1000 // Fetch all dealers, we'll handle rotation client-side
        });
        console.log('✅ [FeaturedCarYards] Featured car yards received:', data);
        const fetchedDealers = data.users || [];

        // Filter out dealers with no listings (same logic as CarDealersPage)
        const activeDealers = fetchedDealers.filter(dealer => 
          dealer._count && dealer._count.listings > 0
        );
        console.log(`🔍 [FeaturedCarYards] Filtered to ${activeDealers.length} dealers with listings (from ${fetchedDealers.length} total)`);

        setAllDealers(activeDealers);
        
        // Set dealers without initial shuffle - rotation will handle shuffling
        setDisplayedDealers(activeDealers.slice(0, 48));
        
        setError(null);
      } catch (err) {
        console.error('❌ [FeaturedCarYards] Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load featured car yards');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedDealers();
  }, []);

  // ========================================
  // AUTO-ROTATION (Every 77 minutes)
  // ========================================
  useEffect(() => {
    if (allDealers.length === 0) return;

    const rotationInterval = 77 * 60 * 1000; // 77 minutes in milliseconds
    
    const intervalId = setInterval(() => {
      console.log('🔄 [FeaturedCarYards] Auto-rotating dealers (77 min interval)');
      const shuffled = shuffleArray(allDealers);
      setDisplayedDealers(shuffled.slice(0, 48));
      setRotationKey(Date.now()); // Force re-render
      
      // Reset to random sort when rotation happens
      if (sortOption !== 'random') {
        setSortOption('random');
      }
    }, rotationInterval);

    return () => clearInterval(intervalId);
  }, [allDealers, sortOption]);

  // ========================================
  // FILTERED AND SORTED DEALERS
  // ========================================
  const filteredAndSortedDealers = useMemo(() => {
    let filtered = displayedDealers;

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(dealer => 
        dealer.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dealer.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply sorting
    let sorted = [...filtered];
    switch (sortOption) {
      case 'name-asc':
        sorted.sort((a, b) => a.fullName.localeCompare(b.fullName));
        break;
      case 'name-desc':
        sorted.sort((a, b) => b.fullName.localeCompare(a.fullName));
        break;
      case 'random':
      default:
        // Already shuffled, keep as is
        break;
    }

    return sorted;
  }, [displayedDealers, searchQuery, sortOption, rotationKey]);

  // ========================================
  // RENDER
  // ========================================
  return (
    <section className="py-6 md:py-10">
      <h2 className="text-base sm:text-lg md:text-xl font-normal text-center mb-1 sm:mb-2 flex items-center justify-center gap-2">
        <Star className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" /> Featured Car Yards
      </h2>
      <p className="text-xs sm:text-sm md:text-base text-muted-foreground text-center mb-4 sm:mb-6">
        Connect with our top-tier Gold and Platinum vehicle dealers.
      </p>

      {/* Search and Sort Controls */}
      {!loading && allDealers.length > 0 && (
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search car yards by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 text-xs sm:text-sm h-9 sm:h-10"
            />
          </div>

          {/* Sort Dropdown */}
          <div className="w-full sm:w-48">
            <Select value={sortOption} onValueChange={setSortOption}>
              <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="random" className="text-xs sm:text-sm">Random Order</SelectItem>
                <SelectItem value="name-asc" className="text-xs sm:text-sm">Name (A-Z)</SelectItem>
                <SelectItem value="name-desc" className="text-xs sm:text-sm">Name (Z-A)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {error && (
        <p className="text-destructive text-center mb-4 text-xs sm:text-sm">Error: {error}</p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 md:gap-4">
        {loading && Array.from({ length: 8 }).map((_, i) => (
          <AgentCardSkeleton key={i} />
        ))}
        
        {!loading && filteredAndSortedDealers.map((dealer) => (
          <AgentCard 
            key={`${dealer.id}-${rotationKey}`}
            agentId={dealer.id}
            name={dealer.fullName}
            email={dealer.email}
            isCarYard={true}
          />
        ))}
      </div>

      {!loading && filteredAndSortedDealers.length === 0 && searchQuery && (
        <div className="text-center py-8 sm:py-12 text-muted-foreground">
          <Search className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 sm:mb-3" />
          <p className="text-sm sm:text-base">No car yards found matching "{searchQuery}"</p>
          <button 
            onClick={() => setSearchQuery('')}
            className="text-xs sm:text-sm text-primary hover:underline mt-2"
          >
            Clear search
          </button>
        </div>
      )}

      {!loading && allDealers.length === 0 && (
        <div className="text-center py-8 sm:py-12 text-muted-foreground">
          <p className="text-sm sm:text-base">No featured car yards available at the moment.</p>
        </div>
      )}
    </section>
  );
};

// ========================================
// MAIN COMPONENT
// ========================================
export default function VehiclesPage() {
  console.log('🎨 [VehiclesPage] Component mounted');

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <CategoryToggle />
        <VehiclesSearchFilter />

        <div className="my-6 sm:my-8">
          <Separator />
        </div>

        <FeaturedCarYards />
      </main>
      <Footer />
    </div>
  );
}